/**
 * Workspace 상태 관리 스토어 (Zustand)
 *
 * 여러 가계부(워크스페이스)를 관리하고 전환하는 기능을 제공합니다.
 * localStorage에 자동으로 저장되어 브라우저를 닫았다 열어도 설정이 유지됩니다.
 *
 * @example
 * ```tsx
 * import { useWorkspaceStore } from '@/core/workspace/workspaceStore'
 *
 * function WorkspaceSelector() {
 *   const { workspaces, activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore()
 *
 *   return (
 *     <select value={activeWorkspaceId || ''} onChange={(e) => setActiveWorkspace(e.target.value)}>
 *       {workspaces.map(ws => (
 *         <option key={ws.id} value={ws.id}>{ws.name}</option>
 *       ))}
 *     </select>
 *   )
 * }
 * ```
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Workspace, WorkspaceInput } from './types'

/**
 * Workspace Store 인터페이스
 */
interface WorkspaceStore {
  // ========== 상태 ==========

  /**
   * 전체 워크스페이스 목록
   */
  workspaces: Workspace[]

  /**
   * 현재 활성화된 워크스페이스 ID
   */
  activeWorkspaceId: string | null

  // ========== 계산된 값 (Computed) ==========

  /**
   * 현재 활성화된 워크스페이스 객체 반환
   *
   * @returns 활성 워크스페이스 또는 null (없는 경우)
   */
  activeWorkspace: () => Workspace | null

  // ========== 액션 (Actions) ==========

  /**
   * 활성 워크스페이스 변경
   *
   * @param id - 활성화할 워크스페이스 ID
   *
   * @example
   * setActiveWorkspace('ws_1704067200000')
   */
  setActiveWorkspace: (id: string) => void

  /**
   * 새 워크스페이스 추가
   *
   * @param input - 워크스페이스 생성 데이터
   * @returns 생성된 워크스페이스
   *
   * @example
   * const workspace = addWorkspace({
   *   name: '부부 가계부',
   *   spreadsheetId: '1ABC...XYZ'
   * })
   */
  addWorkspace: (input: WorkspaceInput) => Workspace

  /**
   * 워크스페이스 정보 수정
   *
   * @param id - 수정할 워크스페이스 ID
   * @param updates - 수정할 필드 (부분 업데이트)
   *
   * @example
   * updateWorkspace('ws_123', { name: '새 이름' })
   */
  updateWorkspace: (id: string, updates: Partial<WorkspaceInput>) => void

  /**
   * 워크스페이스 삭제
   *
   * 활성 워크스페이스 삭제 시 자동으로 첫 번째 워크스페이스로 전환합니다.
   *
   * @param id - 삭제할 워크스페이스 ID
   *
   * @example
   * deleteWorkspace('ws_123')
   */
  deleteWorkspace: (id: string) => void
}

/**
 * Workspace Store 생성
 *
 * Zustand의 persist 미들웨어를 사용하여 localStorage에 자동 저장합니다.
 * themeStore.ts와 동일한 패턴을 따릅니다.
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      // ========== 초기 상태 ==========
      workspaces: [],
      activeWorkspaceId: null,

      // ========== 계산된 값 ==========
      activeWorkspace: () => {
        const { workspaces, activeWorkspaceId } = get()
        return workspaces.find((ws) => ws.id === activeWorkspaceId) || null
      },

      // ========== 액션 ==========
      setActiveWorkspace: (id) => {
        set({ activeWorkspaceId: id })
      },

      addWorkspace: (input) => {
        const newWorkspace: Workspace = {
          id: `ws_${Date.now()}`,
          name: input.name,
          spreadsheetId: input.spreadsheetId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        set((state) => ({
          workspaces: [...state.workspaces, newWorkspace],
          // 첫 번째 워크스페이스는 자동으로 활성화
          activeWorkspaceId: state.workspaces.length === 0 ? newWorkspace.id : state.activeWorkspaceId,
        }))

        return newWorkspace
      },

      updateWorkspace: (id, updates) => {
        set((state) => ({
          workspaces: state.workspaces.map((ws) =>
            ws.id === id
              ? {
                  ...ws,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                }
              : ws
          ),
        }))
      },

      deleteWorkspace: (id) => {
        set((state) => {
          const filteredWorkspaces = state.workspaces.filter((ws) => ws.id !== id)

          // 활성 워크스페이스가 삭제되면 첫 번째 워크스페이스로 전환
          const newActiveId =
            state.activeWorkspaceId === id
              ? filteredWorkspaces[0]?.id || null
              : state.activeWorkspaceId

          return {
            workspaces: filteredWorkspaces,
            activeWorkspaceId: newActiveId,
          }
        })
      },
    }),
    {
      name: 'workspace-storage', // localStorage key
    }
  )
)

/**
 * 현재 활성 워크스페이스의 spreadsheetId 가져오기
 *
 * Repository에서 사용할 수 있도록 함수로 export합니다.
 * Store 외부에서 호출 가능합니다.
 *
 * @returns 현재 활성 워크스페이스의 spreadsheetId (없으면 빈 문자열)
 *
 * @example
 * ```typescript
 * import { getCurrentSpreadsheetId } from '@/core/workspace/workspaceStore'
 *
 * const spreadsheetId = getCurrentSpreadsheetId()
 * const client = new GoogleSheetsClient(spreadsheetId)
 * ```
 */
export function getCurrentSpreadsheetId(): string {
  const activeWorkspace = useWorkspaceStore.getState().activeWorkspace()
  return activeWorkspace?.spreadsheetId || ''
}
